<script lang="ts">
    import type * as t from "./types";
    import { Confetti } from "svelte-confetti";
    import { capitalizeFirstLetter, getPrizeText } from "./utils";

    export let stepsCount: number;
    export let currentStep: number;
    export let prize: t.SegmentConfig["prize"];

    const bullets: HTMLElement[] = [];
    const connectionLines: HTMLElement[] = [];
    let prizeText = getPrizeText(prize);
    prizeText = capitalizeFirstLetter(prizeText);

    let completed = false;
    let prizeTextEl: HTMLElement;

    function setupBulletsRefs(element: HTMLElement) {
        bullets.push(element);
    }

    function setupConnectionLinesRefs(element: HTMLElement) {
        connectionLines.push(element);
    }

    function handleChangeCurrentStep(currentStep: number) {
        if (connectionLines.length === 0) {
            return;
        }

        const connectionLine = connectionLines[currentStep - 1];
        connectionLine.ontransitionend = () => {
            connectionLine.parentElement?.classList.add("items-center");
        };
        connectionLine.classList.replace("w-0", "w-full");
    }

    async function handleConnectionLineTransitionEnd() {
        const bulletBorder = bullets[currentStep];
        const bullet = bulletBorder?.firstElementChild;
        if (bulletBorder != null) {
            bulletBorder.classList.replace(
                "bullet-border-upcoming",
                "bullet-border-current",
            );
            bullet?.classList.replace("bullet-upcoming", "bullet-current");
        }

        const previousBulletBorder = bullets[currentStep - 1];
        if (previousBulletBorder != null) {
            const previousBullet = previousBulletBorder.firstElementChild;

            previousBulletBorder.classList.replace(
                "bullet-border-current",
                "bullet-border-complete",
            );
            previousBullet?.classList.replace(
                "bullet-current",
                "bullet-complete",
            );
        }

        // Handle last step
        if (currentStep < stepsCount - 1) {
            return;
        }

        completed = true;
        prizeTextEl.innerHTML = `You've earned ${prizeText}!`;
    }

    $: handleChangeCurrentStep(currentStep);
</script>

<div class="flex w-full items-center justify-between">
    {#each Array(stepsCount) as _, i (i)}
        {#if i > 0}
            <div
                class="connection-line-container flex h-full flex-1 flex-col justify-center"
            >
                <div
                    use:setupConnectionLinesRefs
                    on:transitionend={handleConnectionLineTransitionEnd}
                    class="h-px w-0 bg-[var(--primary-color)] transition-all"
                ></div>
            </div>
        {/if}
        {#if i < stepsCount - 1}
            <div
                use:setupBulletsRefs
                class={`${i === 0 ? "bullet-border-current" : "bullet-border-upcoming"} rounded-full border-2 transition-all`}
            >
                <div
                    class={`${i === 0 ? "bullet-current" : "bullet-upcoming"} rounded-full transition-all`}
                ></div>
            </div>
        {/if}
        {#if i >= stepsCount - 1}
            <div
                class="rounded-lg border-2 border-[var(--accent-color)] p-2 pl-2 text-sm font-semibold transition-all"
                class:border-0={completed}
                class:bg-[var(--primary-color)]={completed}
                class:text-[var(--secondary-color)]={!completed}
                class:text-[var(--text-on-primary-color)]={completed}
            >
                <span bind:this={prizeTextEl}>
                    {prizeText}
                </span>
                {#if completed}
                    <div class="flex w-full justify-center">
                        <Confetti cone delay={[0, 250]} />
                    </div>
                {/if}
            </div>
        {/if}
    {/each}
</div>

<style>
    .connection-line-container {
        background-color: rgb(var(--primary-color-rgb) / 0.2);
    }
    .bullet-border-upcoming {
        padding: 4px;
        border-color: var(--primary-color);
        filter: grayscale(100%) opacity(20%);
    }
    .bullet-border-current {
        padding: 4px;
        border-color: var(--accent-color);
    }
    .bullet-border-complete {
        padding: 0px;
        border-width: 0;
    }
    .bullet-upcoming {
        width: 8px;
        height: 8px;
        background-color: transpaernt;
    }
    .bullet-current {
        width: 8px;
        height: 8px;
        background-color: var(--primary-color);
    }
    .bullet-complete {
        width: 16px;
        height: 16px;
        background-color: var(--primary-color);
    }
</style>
